import * as Yup from 'yup';
import {ID, IModel} from '~/lib/model';
import {Configuration, ConfigurationSchema, defaultConfiguration} from '~/modules/Clients/models/Configuration';
import {FeeOverrideSettingSchema, IFeeOverrideSetting} from '~/modules/Sources/models/FeeOverrideSetting';
import {ISourceDomains, SourceDomainsSchema} from '~/modules/Sources/models/IDomains';
import {IPlacement, PlacementSchema} from '~/modules/Sources/models/Placement';

export type ChargeSchema = string;
export type TrafficSource = string;
export type SourceCategory = string;

export enum RoundingAdType {
    BANNER = 'banner',
    VIDEO = 'video',
    NATIVE = 'native',
    INTERSTITIAL = 'interstitial'
}

export const roundingAdTypes: { value: RoundingAdType; label: string }[] = [
    {value: RoundingAdType.BANNER, label: 'Banner'},
    {value: RoundingAdType.VIDEO, label: 'Video'},
    {value: RoundingAdType.NATIVE, label: 'Native'},
    {value: RoundingAdType.INTERSTITIAL, label: 'Interstitial'}
];

export const roundingName = (type: RoundingAdType) => 'priceRounding.' + type;

export const roundingLabel = (type: RoundingAdType) => roundingAdTypes.find(item => item.value === type).label;

export const roundingFieldNames = () => {
    let names: string[] = [];
    Object.values(RoundingAdType).map(type => {
        names.push('priceRounding.' + type);
    });
    return names.join(', ');
};

const roundingValidation = (value) => {
    if (value !== undefined) {
        let parts = value.replace(/^\s+|\s+$/g, '').split('\n');
        let errors = 0;
        parts.map(part => {
            if (!part.match(/^-?\d*(\.\d+)?$/)) {
                errors++;
            }
        });
        if (errors) {
            return false;
        }
    }
    return true;
};

export type PriceRounding = {
    banner?: number[] | string;
    video?: number[] | string;
    native?: number[] | string;
    interstitial?: number[] | string;
};

export interface ISourcePreview {
  id: number;
  name: string;
}

export interface ISource extends IModel {
    check: boolean;
    sellerId: ID;
    name: string;
    tags: string[];
    ks: string;
    fee: number;
    tmax: number;
    bcat: string[];
    bapp: string[];
    badv: string[];
    scale?: number | null;
    active: boolean;
    nochargeAgencies: number[];
    noimportAgencies: number[];
    directRelationAgencies: number[];
    maxRps?: number | null;
    chargeSchema: ChargeSchema;
    trafficSource: TrafficSource;
    configuration: Configuration;
    feeOverrideConfiguration?: IFeeOverrideSetting[];
    priceRounding?: PriceRounding;
    category: SourceCategory;
    domain?: string;
    integrationSourceId?: string;
    domains: ISourceDomains[];
    placements: IPlacement[];
}

export interface ISourceTags {
    sourceId: ID
    tags: string[]
}

export interface ILimitedSource extends IModel {
    sellerId: ID;
    name: string;
    active: boolean;
    priceRounding?: PriceRounding;
}

const roundingMessage = 'Invalid ranges. Use only one number per line. Decimal points should be separated by a dot.';

export const SourceSchema = Yup.object().shape<ISource>({
    check: Yup.boolean().nullable(),
    id: Yup.number().nullable(),
    sellerId: Yup.number().required().nullable(),
    name: Yup.string().required(),
    tags: Yup.array<string>().nullable(),
    ks: Yup.string().nullable(),
    fee: Yup.number().min(0).required('is required'),
    tmax: Yup.number().min(0).required('is required'),
    bcat: Yup.array<string>().nullable(),
    bapp: Yup.array<string>().nullable(),
    badv: Yup.array<string>().nullable(),
    scale: Yup.number().min(0).nullable(),
    active: Yup.boolean().required(),
    nochargeAgencies: Yup.array<number>().nullable(),
    noimportAgencies: Yup.array<number>().nullable(),
    directRelationAgencies: Yup.array<number>().nullable(),
    chargeSchema: Yup.string().required().nullable(),
    trafficSource: Yup.string().required().nullable(),
    maxRps: Yup.number().min(0).nullable(),
    configuration: ConfigurationSchema('configuration'),
    feeOverrideConfiguration: Yup.array().of(FeeOverrideSettingSchema),
    priceRounding: Yup.object().shape({
        banner: Yup.string().test(roundingName(RoundingAdType.BANNER), roundingMessage, (value: string) => roundingValidation(value)),
        video: Yup.string().test(roundingName(RoundingAdType.VIDEO), roundingMessage, (value: string) => roundingValidation(value)),
        native: Yup.string().test(roundingName(RoundingAdType.NATIVE), roundingMessage, (value: string) => roundingValidation(value)),
        interstitial:
            Yup.string().test(roundingName(RoundingAdType.INTERSTITIAL), roundingMessage, (value: string) => roundingValidation(value))
    }),
    category: Yup.string().required().nullable(),
    domain: null,
    integrationSourceId: Yup.string().nullable(),
    domains: Yup.array().of(SourceDomainsSchema),
    placements: Yup.array().of(PlacementSchema),
});

export interface ISourceSummary {
    id: number;
    sellerId: number;
    name: string | null;
    tags: string[];
    tmax: number | null;
    active: boolean | null;
    check?: boolean;
}

export const adaptSourceSummary = (source: any): ISourceSummary => ({
    id: source?.id ?? null,
    sellerId: source?.sellerId ?? null,
    name: source?.name ?? null,
    tags: Array.isArray(source?.tags) ? source.tags : [],
    tmax: source?.tmax ?? null,
    active: source?.active ?? null,
    check: Boolean(source?.check)
});

export const defaultSource: ISource = {
    check: false,
    id: null,
    sellerId: null,
    name: '',
    tags: [],
    ks: '',
    fee: 0,
    tmax: 0,
    bcat: [],
    bapp: [],
    badv: [],
    scale: null,
    active: false,
    nochargeAgencies: [],
    noimportAgencies: [],
    directRelationAgencies: [],
    maxRps: null,
    chargeSchema: null,
    trafficSource: null,
    configuration: defaultConfiguration,
    feeOverrideConfiguration: [],
    priceRounding: {
        banner: '',
        video: '',
        native: '',
        interstitial: ''
    },
    category: 'Not Specified',
    domain: '',
    integrationSourceId: null,
    domains: [],
    placements: []
};
